import { useJobillaOrgId } from '@/hooks'
import {
	Button,
	ConfirmationDialog,
	type ConfirmationDialogRef,
	FormDialog,
	SettingsNav,
	SettingsSubheading,
	SubtleLoader,
} from '@/ui'
import {
	type AdDesignData,
	useAdDesignDestroy,
	useAdDesignIndex,
	useAdDesignShowDefaults,
	useAdDesignUpdate,
	useAdDesignUpdateDefaults,
} from '@jobilla/nexus-sdk'
import { useEffect, useRef, useState } from 'react'
import { useAdDesignInvalidation } from '../useAdDesignInvalidation'
import { GalleryGrid } from './gallery-grid'

export function DesignGalleryPage() {
	const organisationId = useJobillaOrgId()
	const { data: designs, isLoading } = useAdDesignIndex(organisationId ?? '')
	const { data: defaults } = useAdDesignShowDefaults(organisationId ?? '')
	const invalidate = useAdDesignInvalidation()
	const confirmRef = useRef<ConfirmationDialogRef>(null)

	const [renaming, setRenaming] = useState<AdDesignData | null>(null)
	const [renameDraft, setRenameDraft] = useState('')

	useEffect(() => {
		if (renaming) { setRenameDraft(renaming.name) }
	}, [renaming])

	const updateMutation = useAdDesignUpdate({
		onSuccess: async () => {
			await invalidate(organisationId)
			setRenaming(null)
		},
	})

	const destroyMutation = useAdDesignDestroy({
		onSuccess: async () => {
			await invalidate(organisationId)
		},
	})

	const defaultsMutation = useAdDesignUpdateDefaults({
		onSuccess: () => invalidate(organisationId),
	})

	const handleRename = (design: AdDesignData) => {
		setRenaming(design)
	}

	const handleRenameSubmit = () => {
		if (!organisationId || !renaming || !renameDraft.trim()) { return }
		updateMutation.mutate({
			organisation: organisationId,
			adDesign: renaming.id,
			data: { name: renameDraft.trim() },
		})
	}

	const handleDelete = async (design: AdDesignData) => {
		if (!organisationId) { return }
		try {
			await confirmRef.current?.confirm({
				title: 'Delete design',
				message:
					`Are you sure you want to delete "${design.name}"? This action cannot be undone, but it will not affect existing campaigns.`,
				confirmText: 'Delete',
			})
			destroyMutation.mutate({
				organisation: organisationId,
				adDesign: design.id,
			})
		} catch {
			// cancelled
		}
	}

	const handleSetDefault = (design: AdDesignData, placement: 'feed' | 'story') => {
		if (!organisationId || !defaults) { return }
		const currentFeedId = defaults.feed?.id ?? null
		const currentStoryId = defaults.story?.id ?? null

		defaultsMutation.mutate({
			organisation: organisationId,
			data: {
				defaultFeedAdDesignId: placement === 'feed'
					? (currentFeedId === design.id ? null : design.id)
					: currentFeedId,
				defaultStoryAdDesignId: placement === 'story'
					? (currentStoryId === design.id ? null : design.id)
					: currentStoryId,
			},
		})
	}

	return (
		<SettingsNav active="ad-designs" fullWidth={true}>
			<section className="space-y-6 py-8 px-10">
				<SettingsSubheading>Ad Designs</SettingsSubheading>
				<p className="text-sm text-grey-500">
					Manage your organisation&apos;s saved ad designs. Set defaults to automatically apply them to new
					campaigns.
				</p>
				{isLoading
					? (
						<div className="py-12 flex justify-center">
							<SubtleLoader />
						</div>
					)
					: (
						<GalleryGrid
							designs={designs ?? []}
							defaults={defaults}
							onRename={handleRename}
							onDelete={handleDelete}
							onSetDefaultFeed={(design) => handleSetDefault(design, 'feed')}
							onSetDefaultStory={(design) => handleSetDefault(design, 'story')}
							showActions={true}
							emptyState={
								<div className="rounded-lg border-2 border-dashed border-grey-200 p-12 text-center">
									<p className="text-sm text-grey-400">
										No designs yet. Save a design from the Design Simulator to see it here.
									</p>
								</div>
							}
						/>
					)}
			</section>

			<FormDialog
				open={renaming !== null}
				onClose={() => {
					if (!updateMutation.isPending) { setRenaming(null) }
				}}
				title="Rename design"
			>
				<form
					onSubmit={(e) => {
						e.preventDefault()
						handleRenameSubmit()
					}}
				>
					<fieldset disabled={updateMutation.isPending}>
						<input
							type="text"
							value={renameDraft}
							onChange={(e) => setRenameDraft(e.target.value)}
							className="w-full rounded border border-grey-200 px-3 py-2 text-sm text-grey-800 outline-none focus:border-brand-400 focus:ring-1 focus:ring-brand-400"
							autoFocus={true}
						/>
						<div className="flex justify-end gap-3 mt-4">
							<Button type="subtle" size="normal" onClick={() => setRenaming(null)}>
								Cancel
							</Button>
							<Button
								type="committal"
								size="normal"
								submit={true}
								disabled={!renameDraft.trim()}
								isLoading={updateMutation.isPending}
							>
								Save
							</Button>
						</div>
					</fieldset>
				</form>
			</FormDialog>
			<ConfirmationDialog ref={confirmRef} />
		</SettingsNav>
	)
}
